import { SupportHost, SupportContactButton } from './components/support/Support'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { logInfo, flushLogs } from './lib/betterstack-logger'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ActivityProvider } from './contexts/ActivityContext'
import { ToastContainer } from 'react-toastify'
import App from './App.jsx'
import PWAApp from './PWAApp.jsx'
import './index.css'
import 'react-toastify/dist/ReactToastify.css'
import { initErrorReporter, reportError } from './utils/error-reporter'
import { TRANSIENT_NETWORK_ERRORS, BROWSER_EXTENSION_URLS, scrubEvent, scrubReplayEvent, replayEventProcessor } from './lib/sentry-capture'
import { registerServiceWorker } from './lib/service-worker'

// Initialize the single global error reporter (TASK-052A). It registers the
// window 'error'/'unhandledrejection' handlers and reports via the deployed
// /.netlify/functions/error-report relay (Slack + Supabase error_log + Better Stack).
initErrorReporter()

// Initialize Sentry if DSN is provided
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
if (SENTRY_DSN) {
  // Detect environment from URL
  const hostname = window.location.hostname
  const environment = hostname.includes('uat') || hostname.includes('staging')
    ? 'uat'
    : hostname.includes('localhost') || hostname.includes('127.0.0.1')
    ? 'development'
    : 'production'

  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    // Drop transient client-network noise (offline / CORS / DNS / abort-on-nav /
    // extension-interference FETCH FAILURES) so it stops auto-filing Triage cards
    // (OPS-688). Scoped to the raw browser-thrown fetch-rejection messages only —
    // handled API errors surface with their own messages and still report. See
    // TRANSIENT_NETWORK_ERRORS. For errors thrown BY extension code (a different
    // problem with a different owner), see denyUrls just below.
    ignoreErrors: TRANSIENT_NETWORK_ERRORS,
    // Drop errors thrown BY a third-party browser extension injected into the
    // page (OPS-1017 / OPS-1021). Matched on the throwing frame's script URL, not
    // on the message — the message shape is one our own code produces too. See
    // BROWSER_EXTENSION_URLS.
    denyUrls: BROWSER_EXTENSION_URLS,
    // Scrub live-credential values (e.g. the /auth/confirm `token_hash`) out of
    // every URL an event carries — request.url + navigation breadcrumbs — before
    // it leaves the browser (OPS-803). Generic via the redactUrl denylist; also
    // guards Session Replay, whose recorded navigation URLs derive from these
    // breadcrumbs. The address-bar token itself is additionally wiped on mount by
    // Confirm.jsx's history.replaceState, so Replay's initial-URL snapshot is
    // clean too.
    // scrubEvent = scrubEventUrls + value redaction of messages/exceptions/
    // breadcrumbs/extra/contexts/tags (OPS-1470).
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        beforeAddRecordingEvent: scrubReplayEvent,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'unknown',
  })
  // beforeSend* never runs for `replay_event`; scrub its urls[] / request.url here.
  Sentry.addEventProcessor(replayEventProcessor)
  console.log('✅ Sentry initialized for environment:', environment)
} else {
  console.log('ℹ️ Sentry not initialized (VITE_SENTRY_DSN not set)')
}

// Initialize Better Stack browser logging
logInfo('app-init', { sentry: !!SENTRY_DSN })

// Flush logs before page unload
window.addEventListener('beforeunload', () => flushLogs())

// Detect if running as installed PWA
const isPWA = window.matchMedia('(display-mode: standalone)').matches
  || window.navigator.standalone // iOS Safari
  || document.referrer.includes('android-app://') // Android TWA

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Register Service Worker for PWA support. All failure paths (unsupported
// browser, registration failure, and the hourly update re-fetch) are handled
// inside the helper — the un-caught `registration.update()` rejection used to
// auto-file a Linear card on any transient network blip (OPS-1034).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    registerServiceWorker()
  })
}

// Auto-reload on stale chunk errors after deploy
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <>
  <SupportHost />
  <Sentry.ErrorBoundary
    onError={(error, componentStack, eventId) => reportError(error.message, { sentryId: eventId, stack: (error.stack || "") + (componentStack || ""), userAction: "ErrorBoundary" })}
    fallback={({ error, resetError }) => (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Something went wrong</h1>
        <p style={{ color: '#666', marginTop: '1rem' }}>{error.message}</p>
        <button 
          onClick={resetError}
          style={{ 
            marginTop: '1rem', 
            padding: '0.5rem 1rem', 
            cursor: 'pointer',
            borderRadius: '4px',
            border: '1px solid #ccc'
          }}
        >
          Try again
        </button>
        <SupportContactButton />
      </div>
    )}
    showDialog={false}
  >
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <ActivityProvider>
              {isPWA ? <PWAApp /> : <App />}
            </ActivityProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
    {/* App-wide toast outlet — without this every toast.*() call is a silent no-op (OPS-350). */}
    <ToastContainer position="bottom-right" theme="dark" newestOnTop />
  </Sentry.ErrorBoundary>
  </>
)
